import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import {
  officialEmail,
  communicationAddress,
  permanentAddress,
  personalEmail,
} from "@/lib/employeeContact";
import { downloadProtectedFile, openProtectedFile } from "@/lib/fileAccess";
import { resolveWorkState, workModeLabel, workPlaceLabel } from "@/lib/workPlace";
import { resolvePayrollFlags } from "@/lib/payrollFlags";
import { resolveIncludeInAttendance } from "@/lib/attendanceFlags";
import { formatDisplayDate } from "@/lib/dateFormat";
import AdminLayout from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft,
  User,
  Phone,
  Briefcase,
  ClipboardCheck,
  CalendarDays,
  Wallet,
  FileSignature,
  Laptop,
  History,
  Pencil,
  UserCheck,
  LogOut,
  KeyRound,
  MoreVertical,
  TrendingUp,
  Calendar,
  Plus,
  FileText,
  Eye,
  Download,
  Upload,
  Trash2,
} from "lucide-react";

const STATUS_COLORS = {
  Active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Inactive: "bg-slate-200 text-slate-700 border-slate-300",
  Onboarding: "bg-amber-100 text-amber-700 border-amber-200",
  Probation: "bg-yellow-100 text-yellow-700 border-yellow-200",
  "Notice Period": "bg-orange-100 text-orange-700 border-orange-200",
  Resigned: "bg-rose-100 text-rose-700 border-rose-200",
    // ✅ NEW STATUSES
  Terminated: "bg-red-600 text-white border-red-700",
  Abscond: "bg-gray-700 text-white border-gray-800",
};

const TYPE_COLORS = {
  "Full-time": "bg-blue-100 text-blue-700",
  "Part-time": "bg-sky-100 text-sky-700",
  Contract: "bg-purple-100 text-purple-700",
  Intern: "bg-indigo-100 text-indigo-700",
};

const EVENT_LABELS = {
  hired: "Hired",
  intern_converted: "Intern Converted",
  designation_changed: "Designation Changed",
  department_changed: "Department Changed",
  ctc_changed: "CTC Changed",
  salary_revised: "Salary Revised",
  manager_changed: "Reporting Manager Changed",
  location_changed: "Location Changed",
  work_mode_changed: "Work Place Changed",
  work_location_changed: "Work Location Changed",
  employment_type_changed: "Employment Type Changed",
  status_changed: "Status Changed",
  document_added: "Document Added",
  letter_issued: "Letter Issued",
  exit_initiated: "Exit Initiated",
  exit_finalized: "Exit Finalized",
};

const EVENT_COLORS = {
  hired: "bg-emerald-500",
  intern_converted: "bg-indigo-500",
  designation_changed: "bg-blue-500",
  department_changed: "bg-blue-500",
  ctc_changed: "bg-amber-500",
  salary_revised: "bg-amber-600",
  manager_changed: "bg-sky-500",
  location_changed: "bg-teal-500",
  work_mode_changed: "bg-teal-600",
  work_location_changed: "bg-teal-400",
  employment_type_changed: "bg-violet-500",
  status_changed: "bg-slate-500",
  document_added: "bg-cyan-500",
  letter_issued: "bg-purple-500",
  exit_initiated: "bg-orange-500",
  exit_finalized: "bg-rose-500",
};

const TABS = [
  { id: "overview",   label: "Overview",        icon: User },
  { id: "personal",   label: "Personal",        icon: Phone },
  { id: "job",        label: "Job Details",     icon: Briefcase },
  { id: "attendance", label: "Attendance",      icon: ClipboardCheck },
  { id: "leave",      label: "Leave",           icon: CalendarDays },
  { id: "payroll",    label: "Payroll",         icon: Wallet },
  { id: "documents",  label: "Documents",       icon: FileText },   /*Added by SM */
  { id: "letters",    label: "Letters",         icon: FileSignature },
  { id: "assets",     label: "Assets",          icon: Laptop },
  { id: "timeline",   label: "Career Timeline", icon: History },
];

/** Tabs visible on Self Service My Profile (same page as admin, restricted options). */
export const ESS_PROFILE_TAB_IDS = [
  "overview",
  "personal",
  "job",
  "letters",
  "assets",
  "timeline",
];

/**
 * Shared employee profile UI.
 * - Admin: `/employees/:id`
 * - Self Service: `/me/profile` via `<EmployeeProfile employeeId={…} selfService />`
 * Change this file once; ESS picks up the same look with ESS_PROFILE_TAB_IDS only.
 */
export default function EmployeeProfile({ employeeId: employeeIdProp, selfService = false } = {}) {
  const { id: paramId } = useParams();
  const id = employeeIdProp || paramId;
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin" || user?.role === "hr_executive"
    || Boolean(user?._effective_perms?.employees?.view)
    || Boolean(user?._effective_perms?.employees?.edit);
  const isStrictAdmin = user?.role === "admin" || (user?.role_ids || []).includes("role_admin");
  const showAdminActions = isAdmin && !selfService;
  const canViewDocuments =
    !selfService && (
      isStrictAdmin
      || Boolean(user?._effective_perms?.documents?.view)
      || Boolean(user?._effective_perms?.employees?.view)
    );

const [employee, setEmployee] = useState(null);
const [departments, setDepartments] = useState([]);
const [designations, setDesignations] = useState([]);
const [locations, setLocations] = useState([]);
const [company, setCompany] = useState(null);
const [tab, setTab] = useState("overview");
const [menuOpen, setMenuOpen] = useState(false);
const [uploadOpen, setUploadOpen] = useState(false);

  // Sub-tab data
  const [attendance, setAttendance] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [balances, setBalances] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [letters, setLetters] = useState([]);
  const [assets, setAssets] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);

  // Modals
  const [exitOpen, setExitOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetPassword, setResetPassword] = useState("Welcome@123");
  const [resetBusy, setResetBusy] = useState(false);
  const [resetErr, setResetErr] = useState("");
  const [resetMsg, setResetMsg] = useState("");

  const openPasswordReset = () => {
    setMenuOpen(false);
    setResetPassword("Welcome@123");
    setResetErr("");
    setResetMsg("");
    setResetOpen(true);
  };

  const submitPasswordReset = async (e) => {
    e.preventDefault();
    setResetBusy(true);
    setResetErr("");
    setResetMsg("");
    try {
      const usersRes = await api.get("/users");
      const linked = (usersRes.data || []).find((u) => u.employee_id === employee.id);
      if (!linked) {
        setResetErr("No user account is linked to this employee.");
        return;
      }
      await api.post(`/users/${linked.id}/reset-password`, {
        new_password: resetPassword || "Welcome@123",
        must_change_password: true,
      });
      setResetMsg(`Password reset for ${linked.email || linked.id}.`);
      setTimeout(() => setResetOpen(false), 1200);
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      setResetErr(typeof d === "string" ? d : "Failed to reset password");
    } finally {
      setResetBusy(false);
    }
  };

  const load = async () => {
    if (!id) return;
    const [empRes, depRes, desigRes, locRes, allEmpRes, ltRes] = await Promise.all([
      api.get(`/employees/${id}`),
      api.get("/departments"),
      api.get("/designations"),
      api.get("/locations").catch(() => ({ data: [] })),
      api.get("/employees").catch(() => ({ data: [] })),
      api.get("/leave-types").catch(() => ({ data: [] })),
    ]);
    setEmployee(empRes.data);
    setDepartments(depRes.data);
    setDesignations(desigRes.data);
    setLocations(locRes.data);
    setAllEmployees(allEmpRes.data);
    setLeaveTypes(ltRes.data);
    const cid = empRes.data?.company_id || "company";
    const companyRes = await api.get("/company", { params: { company_id: cid } }).catch(() => ({ data: null }));
    setCompany(companyRes.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const visibleTabs = useMemo(() => {
    return TABS.filter((t) => {
      if (selfService && !ESS_PROFILE_TAB_IDS.includes(t.id)) return false;
      if (t.id === "payroll" && employee?.employment?.employment_type === "Intern") return false;
      if (t.id === "documents" && !canViewDocuments) return false;
      return true;
    });
  }, [selfService, canViewDocuments, employee?.employment?.employment_type]);

  useEffect(() => {
    if (visibleTabs.length && !visibleTabs.some((t) => t.id === tab)) {
      setTab(visibleTabs[0].id);
    }
  }, [visibleTabs, tab]);

  const loadTab = async (t) => {
    if (!employee) return;
    if (t === "overview") {
      const today = new Date();
      const month = today.getMonth() + 1;
      const year = today.getFullYear();
      const tasks = [];
      if (attendance.length === 0) {
        tasks.push(
          api.get("/attendance", { params: { month, year } }).then((r) => {
            setAttendance((r.data || []).filter((a) => a.employee_id === employee.id));
          }).catch(() => {})
        );
      }
      if (leaves.length === 0) {
        tasks.push(
          api.get("/leaves", { params: { employee_id: employee.id } }).then((r) => {
            setLeaves(r.data || []);
          }).catch(() => {})
        );
      }
      if (payslips.length === 0 && employee.employment?.employment_type !== "Intern") {
        tasks.push(
          api.get("/payslips", { params: { month, year } }).then((r) => {
            setPayslips((r.data || []).filter((p) => p.employee_id === employee.id));
          }).catch(() => {})
        );
      }
      if (tasks.length) await Promise.all(tasks);
      return;
    }
    if (t === "attendance" && attendance.length === 0) {
      const today = new Date();
      const r = await api.get("/attendance", { params: { month: today.getMonth() + 1, year: today.getFullYear() } });
      setAttendance((r.data || []).filter((a) => a.employee_id === employee.id));
    } else if (t === "leave" && leaves.length === 0) {
      const [lr, br] = await Promise.all([
        api.get("/leaves", { params: { employee_id: employee.id } }).catch(() => ({ data: [] })),
        api.get(`/leave-balances/${employee.id}`).catch(() => ({ data: [] })),
      ]);
      setLeaves(lr.data || []);
      setBalances(br.data || []);
    } else if (t === "payroll" && payslips.length === 0) {
      const today = new Date();
      const r = await api.get("/payslips", { params: { month: today.getMonth() + 1, year: today.getFullYear() } });
      setPayslips((r.data || []).filter((p) => p.employee_id === employee.id));
    } else if (t === "letters" && letters.length === 0) {
      const r = await api.get("/letters").catch(() => ({ data: [] }));
      setLetters((r.data || []).filter((l) => l.employee_id === employee.id));
    } else if (t === "assets" && assets.length === 0) {
      const r = await api.get(`/employees/${employee.id}/assets`).catch(() => ({ data: [] }));
      setAssets(r.data || []);
    } else if (t === "timeline" && timeline.length === 0) {
      const r = await api.get(`/employees/${employee.id}/timeline`).catch(() => ({ data: [] }));
      setTimeline(r.data || []);
    }
  };
  useEffect(() => { loadTab(tab); /* eslint-disable-next-line */ }, [tab, employee]);

  const depMap = useMemo(() => Object.fromEntries(departments.map((d) => [d.id, d])), [departments]);
  const desigMap = useMemo(() => Object.fromEntries(designations.map((d) => [d.id, d])), [designations]);
  const locMap = useMemo(() => Object.fromEntries(locations.map((l) => [l.id, l])), [locations]);
  const empMap = useMemo(
    () => Object.fromEntries(allEmployees.map((e) => [e.id, [e.basic?.first_name, e.basic?.middle_name, e.basic?.last_name].filter(Boolean).join(" ")])),
    [allEmployees]
  );
  const ltMap = useMemo(() => Object.fromEntries(leaveTypes.map((l) => [l.id, l])), [leaveTypes]);

  if (!id || !employee) {
    return (
      <AdminLayout
        title={selfService ? "My Profile" : "Employee Profile"}
        breadcrumbs={selfService ? ["Home", "Self Service", "Profile"] : ["Home", "Employees"]}
      >
        <div className="p-12 text-center text-slate-400">Loading…</div>
      </AdminLayout>
    );
  }

  const fullName = [employee.basic.first_name, employee.basic.middle_name, employee.basic.last_name].filter(Boolean).join(" ");
  const status = employee.employment?.status || "Active";
  const empType = employee.employment?.employment_type || "Full-time";
  const isIntern = empType === "Intern";

  return (
    <AdminLayout
      title={selfService ? "My Profile" : fullName}
      breadcrumbs={
        selfService
          ? ["Home", "Self Service", "Profile"]
          : ["Home", "Employees", employee.basic.code]
      }
    >
      <div className="space-y-4" data-testid={selfService ? "my-profile-page" : "employee-profile-page"}>
        {/* Exit banner — visible everywhere when exit is initiated or finalized */}
        {employee.employment?.exit_initiated_at && (
          <ExitBanner emp={employee} />
        )}

        {/* Header / Hero */}
        <div className="bg-white rounded-lg shadow border border-slate-200 p-5 flex items-start gap-4">
          {!selfService && (
            <button onClick={() => navigate(-1)} className="p-2 text-slate-500 hover:bg-slate-100 rounded" data-testid="profile-back">
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}
          <div className="h-16 w-16 rounded-full bg-[#226DB4] text-white flex items-center justify-center font-semibold text-xl uppercase">
            {employee.basic.first_name?.[0] || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-xl font-semibold text-slate-800">{fullName}</h2>
              <span className={`px-2 py-0.5 rounded text-xs border ${STATUS_COLORS[status] || "bg-slate-100"}`} data-testid="profile-status-badge">{status}</span>
              <span className={`px-2 py-0.5 rounded text-xs ${TYPE_COLORS[empType] || "bg-slate-100"}`}>{empType}</span>
            </div>
            <div className="text-sm text-slate-500 mt-1">
              {employee.basic.code} · {desigMap[employee.employment?.designation_id]?.title || "—"} · {depMap[employee.employment?.department_id]?.name || "—"}
            </div>
            {officialEmail(employee.contact) && (
              <div className="text-xs text-slate-400 mt-1">{officialEmail(employee.contact)} · {employee.contact.phone}</div>
            )}
          </div>

          {showAdminActions && (
            <div className="relative">
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className="p-2 text-slate-600 hover:bg-slate-100 rounded border border-slate-200"
                data-testid="profile-actions-menu"
              >
                <MoreVertical className="h-4 w-4" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-10 w-56 bg-white border border-slate-200 rounded shadow-lg z-10" data-testid="profile-actions-dropdown">
                  <button onClick={() => { setMenuOpen(false); navigate(`/employees?edit=${employee.id}`); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left">
                    <Pencil className="h-3.5 w-3.5" /> Edit Employee
                  </button>
                  {isIntern && (
                    <button onClick={() => { setMenuOpen(false); navigate(`/employees?convert=${employee.id}`); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left" data-testid="profile-convert-intern">
                      <UserCheck className="h-3.5 w-3.5" /> Convert to Permanent
                    </button>
                  )}
                  {!["Resigned", "Inactive", "Terminated", "Abscond"].includes(status) && (
                    <button onClick={() => { setMenuOpen(false); setExitOpen(true); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-rose-50 text-rose-600 text-left" data-testid={status === "Notice Period" ? "profile-finalize-exit" : "profile-initiate-exit"}>
                      <LogOut className="h-3.5 w-3.5" /> {status === "Notice Period" ? "Finalize Exit" : "Initiate Exit"}
                    </button>
                  )}
                  {isStrictAdmin && (
                    <button onClick={openPasswordReset} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left" data-testid="profile-reset-password">
                      <KeyRound className="h-3.5 w-3.5" /> Reset Password
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow border border-slate-200">
          <div className="flex border-b border-slate-200 overflow-x-auto">
            {visibleTabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition whitespace-nowrap ${
                  tab === t.id ? "border-[#226DB4] text-[#226DB4] font-medium" : "border-transparent text-slate-500 hover:text-slate-700"
                }`}
              >
                <t.icon className="h-4 w-4" />
                {t.label}
              </button>
            ))}
          </div>

          <div className="p-5">
            {tab === "overview" && (
              <OverviewTab employee={employee} attendance={attendance} leaves={leaves} payslips={payslips} isIntern={isIntern} />
            )}
            {tab === "personal" && <PersonalTab employee={employee} />}
            {tab === "job" && (
              <JobTab
                employee={employee}
                depMap={depMap}
                desigMap={desigMap}
                locMap={locMap}
                empMap={empMap}
                company={company}
                selfService={selfService}
              />
            )}
            {tab === "attendance" && <AttendanceTab records={attendance} />}
            {tab === "leave" && <LeaveTab leaves={leaves} balances={balances} ltMap={ltMap} />}
            {tab === "payroll" && !isIntern && (
              <PayrollTab payslips={payslips} employeeId={employee.id} selfService={selfService} />
            )}
            {tab === "documents" && <DocumentsTab documents={employee.documents || []} />} 
            {tab === "letters" && <LettersTab letters={letters} selfService={selfService} />}
            {tab === "assets" && <AssetsTab assets={assets} />}
            {tab === "timeline" && (
              <TimelineTab
                events={timeline}
                employeeId={employee.id}
                depMap={depMap}
                desigMap={desigMap}
                locMap={locMap}
                empMap={empMap}
                selfService={selfService}
              />
            )}
          </div>
        </div>
      </div>

      {exitOpen && (
        <ExitModal
          employee={employee}
          onClose={() => setExitOpen(false)}
          onDone={async () => { setExitOpen(false); await load(); setTimeline([]); }}
        />
      )}

      {resetOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="profile-reset-password-modal">
          <form onSubmit={submitPasswordReset} className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
            <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-[#226DB4]" /> Reset Password
            </h3>
            <p className="text-sm text-slate-600">
              Reset the login password for the user linked to this employee. They will be required to change it on next login.
            </p>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">New password</label>
              <input
                type="text"
                required
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
                data-testid="profile-reset-password-input"
              />
            </div>
            {resetErr && <div className="text-sm text-rose-600">{resetErr}</div>}
            {resetMsg && <div className="text-sm text-emerald-600">{resetMsg}</div>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setResetOpen(false)} className="px-4 py-2 text-sm border border-slate-200 rounded">
                Cancel
              </button>
              <button
                type="submit"
                disabled={resetBusy}
                className="px-4 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded disabled:opacity-70"
                data-testid="profile-reset-password-submit"
              >
                {resetBusy ? "Resetting…" : "Reset Password"}
              </button>
            </div>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}

/* ──────── Tab components ──────── */

const Card = ({ children, className = "" }) => (
  <div className={`rounded border border-slate-200 bg-white p-4 ${className}`}>{children}</div>
);
const Field = ({ label, value, testid }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</div>
    <div className="text-sm text-slate-800" data-testid={testid}>{value || <span className="text-slate-400">—</span>}</div>
  </div>
);

function OverviewTab({ employee, attendance, leaves, payslips, isIntern }) {
  const presentDays = attendance.filter((a) => a.status === "Present" || a.status === "WFH").length;
  const exitInitiated = !!employee.employment?.exit_initiated_at;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card><div className="text-xs text-slate-500 mb-1">Days Present (Month)</div><div className="text-2xl font-semibold text-slate-800">{presentDays}</div></Card>
        <Card><div className="text-xs text-slate-500 mb-1">Leave Requests</div><div className="text-2xl font-semibold text-slate-800">{leaves.length}</div></Card>
        {!isIntern && <Card><div className="text-xs text-slate-500 mb-1">Payslips</div><div className="text-2xl font-semibold text-slate-800">{payslips.length}</div></Card>}
        <Card><div className="text-xs text-slate-500 mb-1">Joining Date</div><div className="text-sm font-semibold text-slate-800">{formatDisplayDate(employee.employment?.joining_date)}</div></Card>
      </div>
      {exitInitiated && <ExitDetailsCard emp={employee} />}
    </div>
  );
}

/* ──────── Exit visualization helpers (Phase 15) ──────── */

function deriveExitType(emp) {
  const reason = (emp.employment?.exit_reason || "").toLowerCase();
  const status = emp.employment?.status || "";

  if (status === "Terminated" || reason.includes("termin")) return "Terminated";
  if (status === "Abscond" || reason.includes("abscond")) return "Abscond";
  if (reason.includes("contract ended")) return "Contract End";

  return "Resignation";
}

function ExitBanner({ emp }) {
  const e = emp.employment || {};

  const exitStatus = (e.status || "").trim();

  const isFinalized =
    !!e.exit_finalized_at ||
    ["Resigned", "Terminated", "Abscond"].includes(exitStatus);

  const tone = isFinalized
    ? "bg-rose-50 border-rose-200 text-rose-800"
    : "bg-orange-50 border-orange-200 text-orange-800";

  return (
    <div
      className={`rounded-lg border px-4 py-2.5 text-sm flex flex-wrap items-center gap-x-6 gap-y-1 ${tone}`}
      data-testid="exit-banner"
    >
      <div className="font-semibold">
        {exitStatus === "Abscond"
          ? "Absconded"
          : exitStatus === "Terminated"
          ? "Terminated"
          : isFinalized
          ? "Exit Finalized"
          : "Exit Initiated"}
      </div>

      {e.resignation_date && (
        <div>
          <span className="opacity-70">Resigned:</span>{" "}
          <b>{formatDisplayDate(e.resignation_date)}</b>
        </div>
      )}

      {e.last_working_date && (
        <div>
          <span className="opacity-70">LWD:</span>{" "}
          <b>{formatDisplayDate(e.last_working_date)}</b>
        </div>
      )}

      {typeof e.notice_period_days === "number" && (
        <div>
          <span className="opacity-70">Notice:</span>{" "}
          <b>{e.notice_period_days}d</b>
        </div>
      )}

      <div>
        <span className="opacity-70">Type:</span>{" "}
        <b>{deriveExitType(emp)}</b>
      </div>
    </div>
  );
}

function ExitDetailsCard({ emp }) {
  const e = emp.employment || {};
  const isFinalized = !!e.exit_finalized_at;
  return (
    <Card className="border-orange-200 bg-orange-50/40">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-slate-800" data-testid="exit-details-title">
          Employment / Exit Details
        </h4>
        <span className={`px-2 py-0.5 rounded text-xs border ${isFinalized ? "bg-rose-100 text-rose-700 border-rose-200" : "bg-orange-100 text-orange-700 border-orange-200"}`}>
          {isFinalized ? "Finalized" : "In Progress"}
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Field label="Status" value={e.status} testid="exit-status" />
        <Field label="Exit Type" value={deriveExitType(emp)} testid="exit-type" />
        <Field label="Resignation Date" value={formatDisplayDate(e.resignation_date)} testid="exit-resignation-date" />
        <Field label="Last Working Date" value={formatDisplayDate(e.last_working_date)} testid="exit-lwd-date" />
        <Field label="Notice Period (days)" value={e.notice_period_days ?? "—"} testid="exit-notice" />
        <Field label="Exit Reason" value={e.exit_reason} testid="exit-reason-value" />
        <Field label="Initiated At" value={e.exit_initiated_at ? new Date(e.exit_initiated_at).toLocaleString() : "—"} />
        {isFinalized && (
          <Field label="Finalized At" value={new Date(e.exit_finalized_at).toLocaleString()} />
        )}
      </div>
    </Card>
  );
}

function PersonalTab({ employee }) {
  const c = employee.contact || {};
  const s = employee.salary || {};
  const emergency =
    c.emergency_contact_name
      ? `${c.emergency_contact_name}${c.emergency_contact_phone ? ` (${c.emergency_contact_phone})` : ""}`
      : "";
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="First Name" value={employee.basic.first_name} />
        <Field label="Middle Name" value={employee.basic.middle_name || ""} />
        <Field label="Last Name" value={employee.basic.last_name} />
        <Field label="Gender" value={employee.basic.gender} />
        <Field label="Date of Birth" value={formatDisplayDate(employee.basic.date_of_birth)} />
        <Field label="Blood Group" value={employee.basic.blood_group} />
        <Field label="Marital Status" value={employee.basic.marital_status} />
        <Field label="Official Email" value={officialEmail(c)} />
        <Field label="Personal Email" value={personalEmail(c)} />
        <Field label="Phone" value={c.phone} />
        <Field label="Emergency Contact" value={emergency} />
        <Field label="Communication Address" value={communicationAddress(c)} />
        <Field label="Permanent Address" value={permanentAddress(c)} />
      </div>
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Personal identity (KYC)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="PAN" value={s.pan} testid="personal-pan" />
          <Field label="Aadhaar" value={s.aadhaar} testid="personal-aadhaar" />
        </div>
      </div>
      <div>
        <h4 className="text-sm font-semibold text-slate-700 mb-2">Education</h4>
        {(!employee.education || employee.education.length === 0) ? (
          <div className="text-sm text-slate-400">No education records.</div>
        ) : (
          <div className="border border-slate-200 rounded divide-y divide-slate-100">
            {employee.education.map((ed, i) => (
              <div key={ed.id || i} className="px-3 py-2 text-sm">
                <div className="font-medium text-slate-800">{ed.qualification}</div>
                <div className="text-xs text-slate-500">
                  {[ed.institution, ed.board_university, ed.year, ed.grade, ed.specialization]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function JobTab({ employee, depMap, desigMap, locMap, empMap, company, selfService = false }) {
  const e = employee.employment || {};
  const s = employee.salary || {};
  const mode = e.work_mode || "HeadOffice";
  const place = workPlaceLabel(employee, locMap);
  const state = resolveWorkState(employee, locMap, company);
  const { include_in_payment: includePay, generate_payslip: genSlip } = resolvePayrollFlags(employee);
  return (
    <div className="space-y-6">
      {!selfService && (
        <Link
          to={`/salary-revisions?employee_id=${employee.id}`}
          className="inline-flex text-sm text-[#226DB4] hover:underline"
        >
          Salary revisions
        </Link>
      )}
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Employment</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Employee Code" value={employee.basic.code} />
          <Field label="Employment Type" value={e.employment_type} />
          <Field label="Department" value={depMap[e.department_id]?.name} />
          <Field label="Designation" value={desigMap[e.designation_id]?.title} />
          <Field label="Reporting Manager" value={empMap?.[e.manager_id] || e.reporting_manager} />
          <Field label="Work Place" value={workModeLabel(mode)} />
          <Field label="Place / Branch" value={place} />
          <Field label="State (PT)" value={state} />
          <Field label="Joining Date" value={formatDisplayDate(e.joining_date)} />
          <Field label="Include in attendance" value={resolveIncludeInAttendance(employee) ? "Yes" : "No"} />
          <Field label="Status" value={e.status} />
          {e.internship_end_date && <Field label="Internship End Date" value={formatDisplayDate(e.internship_end_date)} />}
          {e.probation_end_date && <Field label="Probation End Date" value={formatDisplayDate(e.probation_end_date)} />}
          {e.exit_initiated_at && (
            <>
              <Field label="Exit Type" value={deriveExitType(employee)} />
              <Field label="Resignation Date" value={formatDisplayDate(e.resignation_date)} />
              <Field label="Last Working Date" value={formatDisplayDate(e.last_working_date)} />
              <Field label="Notice Period (days)" value={e.notice_period_days ?? "—"} />
              <Field label="Exit Reason" value={e.exit_reason} />
            </>
          )}
        </div>
      </div>
      {!selfService && (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Payroll inclusion</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Include in payment" value={includePay ? "Yes" : "No"} />
            <Field label="Generate payslip" value={includePay && genSlip ? "Yes" : "No"} />
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Include in payment = Generate Payroll. Generate payslip = employee ESS/PDF slip.
            Change under Edit Employee → Salary &amp; Bank.
          </p>
        </div>
      )}
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Bank &amp; Statutory</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="UAN" value={s.uan} testid="job-uan" />
          <Field label="Bank Name" value={s.bank_name} testid="job-bank-name" />
          <Field label="Account Number" value={s.account_number} testid="job-account" />
          <Field label="IFSC" value={s.ifsc} testid="job-ifsc" />
        </div>
        <p className="text-xs text-slate-500 mt-2">
          PAN / Aadhaar are under the <span className="font-medium">Personal</span> tab (identity).
        </p>
      </div>
    </div>
  );
}

function AttendanceTab({ records }) {
  if (records.length === 0) return <div className="text-slate-400 text-sm">No attendance records for this month.</div>;
  return (
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-xs text-slate-600 uppercase">
        <tr><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Check-in</th><th className="px-3 py-2 text-left">Check-out</th><th className="px-3 py-2 text-left">Note</th></tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {records.map((r) => (
          <tr key={r.id}>
            <td className="px-3 py-2">{formatDisplayDate(r.date)}</td>
            <td className="px-3 py-2">{r.status}</td>
            <td className="px-3 py-2">{r.check_in || "—"}</td>
            <td className="px-3 py-2">{r.check_out || "—"}</td>
            <td className="px-3 py-2 text-slate-500">{r.note || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function LeaveTab({ leaves, balances, ltMap }) {
  const resolveLT = (l) =>
    l.leave_type_code ||
    (ltMap && (ltMap[l.leave_type_id]?.code || ltMap[l.leave_type_id]?.name)) ||
    l.leave_type_id ||
    "—";
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {balances.map((b) => {
          const lt = ltMap?.[b.leave_type_id];
          const label = lt?.code || lt?.name || b.leave_type_code || b.code || "—";
          return (
            <Card key={b.leave_type_id || b.id}>
              <div className="text-xs text-slate-500 mb-1" title={lt?.name || ""}>{label}</div>
              <div className="text-xl font-semibold text-slate-800">{b.balance ?? b.remaining ?? "—"}</div>
              <div className="text-[11px] text-slate-400">Used {b.used ?? 0} / {b.entitled ?? 0}</div>
            </Card>
          );
        })}
        {balances.length === 0 && <div className="text-sm text-slate-400 col-span-4">No balance data.</div>}
      </div>
      <div>
        <h4 className="text-sm font-semibold text-slate-700 mb-2">History ({leaves.length})</h4>
        {leaves.length === 0 ? (
          <div className="text-slate-400 text-sm">No leave requests yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-600 uppercase"><tr><th className="px-3 py-2 text-left">Type</th><th className="px-3 py-2 text-left">From</th><th className="px-3 py-2 text-left">To</th><th className="px-3 py-2 text-left">Days</th><th className="px-3 py-2 text-left">Status</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {leaves.map((l) => (
                <tr key={l.id}><td className="px-3 py-2">{resolveLT(l)}</td><td className="px-3 py-2">{formatDisplayDate(l.start_date)}</td><td className="px-3 py-2">{formatDisplayDate(l.end_date)}</td><td className="px-3 py-2">{l.days}</td><td className="px-3 py-2">{l.status}</td></tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function PayrollTab({ payslips, employeeId, selfService = false }) {
  return (
    <div className="space-y-3">
      {employeeId && !selfService && (
        <Link
          to={`/salary-revisions?employee_id=${employeeId}`}
          className="inline-flex text-sm text-[#226DB4] hover:underline"
        >
          Salary revisions
        </Link>
      )}
      {payslips.length === 0 ? (
        <div className="text-slate-400 text-sm">No payslips generated yet.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-600 uppercase">
            <tr>
              <th className="px-3 py-2 text-left">Month</th>
              <th className="px-3 py-2 text-left">Gross</th>
              <th className="px-3 py-2 text-left">Net Pay</th>
              <th className="px-3 py-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {payslips.map((p) => (
              <tr key={p.id}>
                <td className="px-3 py-2">
                  {p.month}/{p.year}
                </td>
                <td className="px-3 py-2">{p.gross_total?.toFixed?.(0)}</td>
                <td className="px-3 py-2">{p.net_pay?.toFixed?.(0)}</td>
                <td className="px-3 py-2">{p.status || "Generated"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function DocumentsTab({ documents }) {
  if (documents.length === 0)
    return <div className="text-slate-400 text-sm">No documents uploaded yet.</div>;

return (
  <div className="space-y-4">

    <div className="flex items-center justify-between">
    <h3 className="text-lg font-semibold text-slate-800">Employee Documents</h3>
    </div>

    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-xs text-slate-600 uppercase">
        <tr>
          <th className="px-4 py-3 text-left">Document Type</th>
          <th className="px-4 py-3 text-left">File Name</th>
          <th className="px-4 py-3 text-left">Uploaded On</th>
          <th className="px-4 py-3 text-center">Actions</th>
        </tr>
      </thead>

      <tbody className="divide-y divide-slate-100">
        {documents.map((d, i) => (
          <tr key={i}>
            <td className="px-4 py-3">{d.name}</td>
            <td className="px-4 py-3">{d.file_name}</td>
            <td className="px-4 py-3">{formatDisplayDate(d.uploaded_at)}</td>

            <td className="px-4 py-3">
              <div className="flex justify-center gap-2">
                <button title="View" onClick={() => d.file_url && openProtectedFile(d.file_url)} className="p-1 rounded hover:bg-slate-100"><Eye className="h-4 w-4 text-slate-600" /></button>
                <button title="Download" onClick={() => d.file_url && downloadProtectedFile(d.file_url, d.file_name)} className="p-1 rounded hover:bg-slate-100"><Download className="h-4 w-4 text-slate-600" /></button>
                
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>

  </div>
);
}

function LettersTab({ letters, selfService = false }) {
  const rows = selfService
    ? (letters || []).filter((l) => !l.approval_status || l.approval_status === "Approved" || l.approval_status === "Issued")
    : letters || [];
  if (rows.length === 0) return <div className="text-slate-400 text-sm">No letters issued yet.</div>;
  const statusLabel = (l) => {
    const s = l.approval_status || "Approved";
    if (s === "Approved" || s === "Issued") return "Issued";
    if (s === "Pending") return "Pending Approval";
    return s;
  };
  return (
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-xs text-slate-600 uppercase"><tr><th className="px-3 py-2 text-left">Type</th><th className="px-3 py-2 text-left">Title</th><th className="px-3 py-2 text-left">Issue Date</th><th className="px-3 py-2 text-left">Status</th></tr></thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((l) => (
          <tr key={l.id}><td className="px-3 py-2">{l.type}</td><td className="px-3 py-2">{l.title}</td><td className="px-3 py-2">{formatDisplayDate(l.issue_date)}</td><td className="px-3 py-2">{statusLabel(l)}</td></tr>
        ))}
      </tbody>
    </table>
  );
}

function AssetsTab({ assets }) {
  if (assets.length === 0) return <div className="text-slate-400 text-sm">No assets assigned.</div>;
  return (
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-xs text-slate-600 uppercase"><tr><th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2 text-left">Type</th><th className="px-3 py-2 text-left">Serial</th><th className="px-3 py-2 text-left">Assigned</th></tr></thead>
      <tbody className="divide-y divide-slate-100">
        {assets.map((a) => (
          <tr key={a.id}><td className="px-3 py-2">{a.name}</td><td className="px-3 py-2">{a.asset_type}</td><td className="px-3 py-2">{a.serial_number}</td><td className="px-3 py-2">{formatDisplayDate(a.assigned_date)}</td></tr>
        ))}
      </tbody>
    </table>
  );
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const TIMELINE_HIDDEN_KEYS = new Set(["backfilled"]);

function formatMoney(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return String(n);
  return `₹ ${num.toLocaleString("en-IN")}`;
}

function formatSalarySnapshot(obj) {
  if (!obj || typeof obj !== "object") return String(obj ?? "—");
  const bits = [];
  if (obj.annual_ctc != null && obj.annual_ctc !== "") bits.push(`CTC ${formatMoney(obj.annual_ctc)}`);
  if (obj.basic != null && obj.basic !== "") bits.push(`Basic ${formatMoney(obj.basic)}`);
  if (obj.hra != null && obj.hra !== "") bits.push(`HRA ${formatMoney(obj.hra)}`);
  if (bits.length) return bits.join(" · ");
  return Object.entries(obj)
    .filter(([, v]) => v != null && v !== "")
    .map(([k, v]) => `${k}: ${typeof v === "number" ? formatMoney(v) : v}`)
    .join(", ") || "—";
}

function TimelineTab({ events, employeeId, depMap, desigMap, locMap, empMap, selfService = false }) {
  const [typeFilter, setTypeFilter] = useState("");
  const [actorFilter, setActorFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [q, setQ] = useState("");

  const friendlyKey = (k) =>
    ({
      code: "Code",
      name: "Name",
      department_id: "Department",
      designation_id: "Designation",
      manager_id: "Reporting Manager",
      location_id: "Location",
      work_mode: "Work Place",
      work_location: "Place / Branch",
      employment_type: "Employment Type",
      joining_date: "Joining Date",
      annual_ctc: "Annual CTC",
      effective_from: "Effective From",
      last_working_date: "Last Working Date",
      resignation_date: "Resignation Date",
      notice_period_days: "Notice Period (days)",
      reason: "Reason",
      finalized_at: "Finalized At",
      field: "Field",
      old: "Previous",
      new: "New",
      old_salary: "Previous salary",
      new_salary: "New salary",
      to_employee_id: "Converted To",
      from_intern_id: "Converted From (Intern)",
      letter_id: "Letter",
      revision_id: "Salary revision",
      type: "Letter Type",
      title: "Letter Title",
      file_name: "File",
      status: "Status",
    })[k] || k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const resolveValue = (key, value, eventType, payload) => {
    if (value == null || value === "") return "—";
    if (key === "revision_id" && typeof value === "string") {
      if (selfService) return "Salary revision";
      return (
        <Link
          to={`/salary-revisions?employee_id=${employeeId || ""}`}
          className="text-[#226DB4] hover:underline"
          title={value}
        >
          Open salary revisions
        </Link>
      );
    }
    if (key === "old_salary" || key === "new_salary") return formatSalarySnapshot(value);
    if (typeof value === "number" && /ctc|salary|amount|basic|hra/i.test(key)) return formatMoney(value);
    if ((key === "old" || key === "new") && typeof value === "number" &&
        (eventType === "ctc_changed" || eventType === "salary_revised" || payload?.field === "annual_ctc")) {
      return formatMoney(value);
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) return value.map((x) => String(x)).join(", ");
      return formatSalarySnapshot(value);
    }
    if (typeof value !== "string") return String(value);

    const fieldHint = payload?.field || key;
    if (eventType === "status_changed" || eventType === "employment_type_changed") return value;

    if (key === "department_id" || fieldHint === "department_id" ||
        (eventType === "department_changed" && (key === "old" || key === "new"))) {
      return depMap?.[value]?.name || value;
    }
    if (key === "designation_id" || fieldHint === "designation_id" ||
        (eventType === "designation_changed" && (key === "old" || key === "new"))) {
      return desigMap?.[value]?.title || value;
    }
    if (key === "location_id" || fieldHint === "location_id" ||
        (eventType === "location_changed" && (key === "old" || key === "new"))) {
      return locMap?.[value]?.name || value;
    }
    if (key === "manager_id" || key === "to_employee_id" || key === "from_intern_id" ||
        fieldHint === "manager_id" ||
        (eventType === "manager_changed" && (key === "old" || key === "new"))) {
      return empMap?.[value] || value;
    }
    if (key === "work_mode" || fieldHint === "work_mode") return workModeLabel(value) || value;
    if (key === "field") return friendlyKey(value);

    // Last resort: look up UUID-looking values in known maps
    if (UUID_RE.test(value)) {
      return depMap?.[value]?.name || desigMap?.[value]?.title || locMap?.[value]?.name || empMap?.[value] || "—";
    }
    return value;
  };

  const eventTypes = useMemo(
    () => [...new Set(events.map((e) => e.event_type))].sort(),
    [events],
  );
  const actors = useMemo(
    () => [...new Set(events.map((e) => e.actor_name || "System"))].sort(),
    [events],
  );

  const rows = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return events.filter((e) => {
      if (typeFilter && e.event_type !== typeFilter) return false;
      if (actorFilter && (e.actor_name || "System") !== actorFilter) return false;
      const day = String(e.occurred_at || "").slice(0, 10);
      if (fromDate && day && day < fromDate) return false;
      if (toDate && day && day > toDate) return false;
      if (ql) {
        const resolvedBits = Object.entries(e.payload || {}).flatMap(([k, v]) => {
          if (TIMELINE_HIDDEN_KEYS.has(k)) return [];
          const dv = resolveValue(k, v, e.event_type, e.payload);
          const text = typeof dv === "string" || typeof dv === "number" ? String(dv) : "";
          return [friendlyKey(k), text];
        });
        const hay = [
          EVENT_LABELS[e.event_type] || e.event_type,
          e.actor_name,
          ...resolvedBits,
          JSON.stringify(e.payload || {}),
        ]
          .join(" ")
          .toLowerCase();
        if (!hay.includes(ql)) return false;
      }
      return true;
    });
  }, [events, typeFilter, actorFilter, fromDate, toDate, q, depMap, desigMap, locMap, empMap, employeeId]);

  if (events.length === 0) return <div className="text-slate-400 text-sm">No timeline events.</div>;

  return (
    <div className="space-y-3" data-testid="career-timeline">
      <div className="flex flex-wrap gap-2 items-end">
        <div className="min-w-[160px] grow">
          <label className="block text-[11px] uppercase text-slate-500 mb-1">Search changes</label>
          <input
            value={q}
            onChange={(ev) => setQ(ev.target.value)}
            placeholder="Department, CTC, actor…"
            className="w-full border border-slate-200 rounded px-3 py-1.5 text-sm"
            data-testid="timeline-search"
          />
        </div>
        <div>
          <label className="block text-[11px] uppercase text-slate-500 mb-1">Event type</label>
          <select
            value={typeFilter}
            onChange={(ev) => setTypeFilter(ev.target.value)}
            className="border border-slate-200 rounded px-2 py-1.5 text-sm bg-white"
            data-testid="timeline-type-filter"
          >
            <option value="">All types</option>
            {eventTypes.map((t) => (
              <option key={t} value={t}>{EVENT_LABELS[t] || t}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[11px] uppercase text-slate-500 mb-1">Actor</label>
          <select
            value={actorFilter}
            onChange={(ev) => setActorFilter(ev.target.value)}
            className="border border-slate-200 rounded px-2 py-1.5 text-sm bg-white"
          >
            <option value="">Anyone</option>
            {actors.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[11px] uppercase text-slate-500 mb-1">From</label>
          <input type="date" value={fromDate} onChange={(ev) => setFromDate(ev.target.value)} className="border border-slate-200 rounded px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-[11px] uppercase text-slate-500 mb-1">To</label>
          <input type="date" value={toDate} onChange={(ev) => setToDate(ev.target.value)} className="border border-slate-200 rounded px-2 py-1.5 text-sm" />
        </div>
        {(typeFilter || actorFilter || fromDate || toDate || q) && (
          <button
            type="button"
            onClick={() => { setTypeFilter(""); setActorFilter(""); setFromDate(""); setToDate(""); setQ(""); }}
            className="text-xs text-slate-600 border border-slate-200 rounded px-2 py-1.5 hover:bg-slate-50"
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="text-xs text-slate-500">{rows.length} of {events.length} events</div>

      <div className="overflow-x-auto border border-slate-200 rounded">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left whitespace-nowrap">When</th>
              <th className="px-3 py-2 text-left">Event</th>
              <th className="px-3 py-2 text-left">Actor</th>
              <th className="px-3 py-2 text-left">Changes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-slate-400">No events match the filters.</td>
              </tr>
            ) : (
              rows.map((e) => (
                <tr key={e.id} data-testid={`timeline-event-${e.event_type}`} className="align-top hover:bg-slate-50/80">
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">
                    {e.occurred_at ? new Date(e.occurred_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className={`inline-block h-2 w-2 rounded-full shrink-0 ${EVENT_COLORS[e.event_type] || "bg-slate-400"}`} />
                      <span className="font-medium text-slate-800">{EVENT_LABELS[e.event_type] || e.event_type}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{e.actor_name || "System"}</td>
                  <td className="px-3 py-2">
                    <div className="space-y-1 text-xs text-slate-700">
                      {Object.entries(e.payload || {})
                        .filter(([k]) => !TIMELINE_HIDDEN_KEYS.has(k))
                        .map(([k, v]) => {
                          let displayKey = friendlyKey(k);
                          let displayValue = v;
                          if ((k === "old" || k === "new") && e.payload?.field) {
                            displayValue = resolveValue(k, v, e.event_type, e.payload);
                          } else {
                            displayValue = resolveValue(k, v, e.event_type, e.payload);
                          }
                          return (
                            <div key={k} className="flex flex-wrap gap-x-1">
                              <span className="text-slate-500">{displayKey}:</span>
                              <span className="font-medium text-slate-800">{displayValue}</span>
                            </div>
                          );
                        })}
                      {(!e.payload || Object.keys(e.payload).filter((k) => !TIMELINE_HIDDEN_KEYS.has(k)).length === 0) && (
                        <span className="text-slate-400">—</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ──────── Exit modal ──────── */
const EXIT_REASONS = [
  "Resignation",
  "Better Opportunity",
  "Higher Studies",
  "Personal / Family",
  "Health Reasons",
  "Relocation",
  "Performance",
  "Termination",
  "Abscond",
  "Contract Ended",
  "Other",
];

/** Maps exit reason (from initiate-exit) to final employment.status on finalize. */
function finalStatusFromReason(reason) {
  const r = (reason || "").toLowerCase();
  if (r.includes("abscond")) return "Abscond";
  if (r.includes("termin")) return "Terminated";
  return "Resigned";
}

function ExitModal({ employee, onClose, onDone }) {
  const status = employee.employment?.status;

  const isFinalizing = status === "Notice Period";
  const isAlreadyFinal = ["Resigned", "Terminated", "Abscond"].includes(status);

  const [form, setForm] = useState({
    resignation_date: new Date().toISOString().slice(0, 10),
    last_working_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    notice_period_days: 30,
    reason: "Resignation",
    remarks: "",
  });

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");

    try {
      if (isFinalizing) {
        await api.post(`/employees/${employee.id}/finalize-exit`);
      } else if (!isAlreadyFinal) {
        // Phase 10 — combine reason dropdown + optional remarks into the reason field
        const payload = { ...form };
        payload.reason = form.remarks
          ? `${form.reason} — ${form.remarks}`
          : form.reason;

        delete payload.remarks;

        await api.post(`/employees/${employee.id}/initiate-exit`, payload);
      }

      await onDone();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <form
        onSubmit={submit}
        className="bg-white rounded-lg shadow-xl w-full max-w-lg"
        data-testid="exit-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h3 className="font-semibold text-slate-800">
            {isFinalizing
              ? "Finalize Exit"
              : isAlreadyFinal
              ? "Exit Completed"
              : "Initiate Exit"}
          </h3>

          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:bg-slate-100 rounded"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-3">
          {err && <div className="text-sm text-rose-600">{err}</div>}

          {isFinalizing ? (
            <div className="text-sm text-slate-700 bg-amber-50 border border-amber-200 rounded p-3">
              This will mark{" "}
              <b>
                {[
                  employee.basic.first_name,
                  employee.basic.middle_name,
                  employee.basic.last_name,
                ]
                  .filter(Boolean)
                  .join(" ")}
              </b>{" "}
              as{" "}
              <b>
                {finalStatusFromReason(employee.employment?.exit_reason)}
              </b>
              , deactivate their login, and append an{" "}
              <b>exit_finalized</b> event to their career timeline. This action
              cannot be undone via UI.
            </div>
          ) : isAlreadyFinal ? (
            <div className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded p-3">
              This employee has already been marked as <b>{status}</b>.
              No further exit action is required.
            </div>
          ) : (
            <>
              <div className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded p-3">
                The employee will move to <b>Notice Period</b> status. Finalize
                on the Last Working Date to fully exit.
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Resignation Date *
                  </label>
                  <input
                    type="date"
                    required
                    className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
                    value={form.resignation_date}
                    onChange={(e) =>
                      setForm({ ...form, resignation_date: e.target.value })
                    }
                    data-testid="exit-resignation-date"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Last Working Date *
                  </label>
                  <input
                    type="date"
                    required
                    className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
                    value={form.last_working_date}
                    onChange={(e) =>
                      setForm({ ...form, last_working_date: e.target.value })
                    }
                    data-testid="exit-lwd"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Notice Period (days)
                </label>
                <input
                  type="number"
                  min={0}
                  className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
                  value={form.notice_period_days}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      notice_period_days: Number(e.target.value),
                    })
                  }
                  data-testid="exit-notice-days"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Reason for Exit *
                </label>
                <select
                  required
                  className="w-full border border-slate-200 rounded px-3 py-2 text-sm bg-white"
                  value={form.reason}
                  onChange={(e) =>
                    setForm({ ...form, reason: e.target.value })
                  }
                  data-testid="exit-reason"
                >
                  {EXIT_REASONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Additional Remarks (optional)
                </label>
                <textarea
                  rows={2}
                  className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
                  value={form.remarks}
                  onChange={(e) =>
                    setForm({ ...form, remarks: e.target.value })
                  }
                  data-testid="exit-remarks"
                  placeholder="Any context or notes about this exit…"
                />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm border border-slate-200 rounded"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={busy || isAlreadyFinal}
            className={`px-4 py-2 text-sm text-white rounded ${
              isFinalizing
                ? "bg-rose-600 hover:bg-rose-700"
                : "bg-orange-500 hover:bg-orange-600"
            }`}
            data-testid="exit-confirm-btn"
          >
            {busy
              ? "Saving…"
              : isFinalizing
              ? "Finalize Exit"
              : isAlreadyFinal
              ? "Completed"
              : "Initiate Exit"}
          </button>
        </div>
      </form>
    </div>
  );
}
